import React from "react";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="privacy-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center">
            <Radio className="w-5 h-5 text-black" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#00F0FF]">DECK</span></div>
        </Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12 prose prose-invert">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// Legal</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mb-6">Privacy Policy</h1>
        <p className="text-[#94A3B8] text-sm mb-8">Last updated: 13 August 2026</p>

        <section className="space-y-6 text-[#F8FAFC] text-sm leading-relaxed">
          <p>
            NeuralDeck ("we", "us") operates the NeuralDeck 4-deck AI DJ studio web application and Android TWA
            (the "Service"). This policy explains what data we collect, why, and your choices.
          </p>

          <h2 className="font-display font-black text-2xl text-white mt-8">What we collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><b>Account data</b>: email, display name, hashed password (bcrypt). Never plain-text passwords.</li>
            <li><b>Spotify connection</b> (optional): if you link Spotify, we store your Spotify access & refresh tokens and your Spotify user id / display name.</li>
            <li><b>Payment data</b>: if you upgrade to PRO, Stripe processes payments on our behalf. We store only Stripe session IDs, subscription IDs and payment status — never card numbers.</li>
            <li><b>Content you create</b>: saved mixes (PRO users), published sets (title, tracklist, audio blob you record and choose to publish), and comments.</li>
            <li><b>Analytics</b>: we use PostHog to record anonymous product analytics and session replays to improve the app. This includes viewport size, clicks, and page views. IPs are truncated.</li>
          </ul>

          <h2 className="font-display font-black text-2xl text-white mt-8">What we do NOT collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Audio from your microphone or camera.</li>
            <li>Your local files — MP3s you upload are processed client-side and never uploaded to our servers unless you explicitly "Publish" a set.</li>
            <li>Your Spotify listening history beyond the playlists you explicitly load in the app.</li>
          </ul>

          <h2 className="font-display font-black text-2xl text-white mt-8">How we use data</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Operate and secure the Service (authentication, subscription state).</li>
            <li>Provide AI harmonic recommendations (track titles, BPMs, keys sent to our LLM provider — OpenAI via Emergent Universal Key — never your personal data).</li>
            <li>Fulfil payments (Stripe) and grant PRO features.</li>
            <li>Publish sets you explicitly choose to publish, on public /set/&lt;id&gt; pages.</li>
          </ul>

          <h2 className="font-display font-black text-2xl text-white mt-8">Third-party services</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><b>Stripe</b> — payments processing. See stripe.com/privacy.</li>
            <li><b>Spotify</b> — music metadata & OAuth. See spotify.com/legal/privacy-policy.</li>
            <li><b>OpenAI</b> (via Emergent Universal Key) — AI recommendations. See openai.com/policies/privacy-policy.</li>
            <li><b>PostHog</b> — anonymous product analytics. See posthog.com/privacy.</li>
          </ul>

          <h2 className="font-display font-black text-2xl text-white mt-8">Your rights</h2>
          <p>
            You can delete your account at any time by contacting us at the address below. On deletion, all your
            personal data, saved mixes, published sets, and Spotify tokens are permanently removed within 30 days.
            You can disconnect Spotify from your account at any time.
          </p>

          <h2 className="font-display font-black text-2xl text-white mt-8">Children</h2>
          <p>
            NeuralDeck is not directed at children under 13. If you believe a child under 13 has created an account,
            contact us and we will delete it.
          </p>

          <h2 className="font-display font-black text-2xl text-white mt-8">Contact</h2>
          <p>
            For any privacy request, contact us at <a href="mailto:privacy@neuraldeck.app" className="text-[#00F0FF] hover:underline">privacy@neuraldeck.app</a>.
          </p>

          <p className="pt-8 text-[#94A3B8]">
            We may update this policy from time to time; when we do, the "Last updated" date at the top will change.
            Material changes will be announced in-app.
          </p>
        </section>

        <div className="mt-16 pt-8 border-t border-[#1E2638] text-center">
          <Link to="/" className="text-[#00F0FF] font-black uppercase tracking-widest text-sm">← Back to NeuralDeck</Link>
        </div>
      </main>
    </div>
  );
}
